import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Scale, ShieldCheck, Megaphone, Plane, Stethoscope, HeartPulse } from "lucide-react";
import AuthShell, { GoogleButton, inputCls, primaryBtn } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const PATHS = { user: "/dashboard", attorney: "/attorney", admin: "/admin", affiliate: "/affiliate", pilot: "/pilot-portal", nurse: "/nurse-portal", telehealth: "/telehealth-portal" };

export function rolesOf(user) {
  return user?.roles || [user?.role].filter(Boolean);
}

export function canAccess(user, portal) {
  const roles = rolesOf(user);
  if (portal === "user") return true;
  if (portal === "pilot") return true; // any authenticated account may onboard/manage as a pilot
  if (portal === "nurse") return true; // RN portal handles its own pending/approval states
  if (portal === "telehealth") return true; // provider portal shows application status; data is server-gated
  if (portal === "admin") return roles.includes("admin");
  if (portal === "attorney") return roles.includes("attorney") && user?.attorney_status !== "suspended";
  if (portal === "affiliate") return roles.includes("affiliate");
  return false;
}

// Best landing spot for generic redirects (highest privilege the user holds).
export function homeFor(user) {
  const roles = rolesOf(user);
  if (roles.includes("admin")) return "/admin";
  if (roles.includes("attorney") && user?.attorney_status !== "suspended") return "/attorney";
  if (roles.includes("affiliate")) return "/affiliate";
  // Pilots/nurses keep the `user` role until admin approval, so route by status too.
  if (roles.includes("pilot") || user?.pilot_status) return "/pilot-portal";
  if (roles.includes("nurse") || user?.nurse_status) return "/nurse-portal";
  if (roles.includes("telehealth") || user?.telehealth_status) return "/telehealth-portal";
  return "/dashboard";
}

const PORTALS = [
  { key: "user", label: "Client", icon: User, title: "Welcome back", subtitle: "Log in to document your accident and build your case." },
  { key: "attorney", label: "Attorney", icon: Scale, title: "Attorney Login", subtitle: "Access your referral board, case files, and invoices." },
  { key: "admin", label: "Admin", icon: ShieldCheck, title: "Admin Login", subtitle: "Manage attorneys, transfers, invoices, and analytics." },
  { key: "affiliate", label: "Affiliate", icon: Megaphone, title: "Affiliate Login", subtitle: "Track referrals, commissions, and payouts." },
  { key: "pilot", label: "Pilot", icon: Plane, title: "Pilot Login", subtitle: "Manage availability, accept missions, and track earnings." },
  { key: "nurse", label: "RN", icon: Stethoscope, title: "RN Login", subtitle: "Access your triage queue, intakes, and verification tools." },
  { key: "telehealth", label: "Provider", icon: HeartPulse, title: "Telehealth Provider Login", subtitle: "Manage client requests, forms, availability, and your practice profile." },
];

export default function Login() {
  const { login, verify2fa, user, loading } = useAuth();
  const navigate = useNavigate();
  const [portal, setPortal] = useState("user");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [twofa, setTwofa] = useState(null); // { challenge_token }
  const [code, setCode] = useState("");
  const redirected = useRef(false);

  useEffect(() => {
    if (!loading && user && !redirected.current) { redirected.current = true; navigate(homeFor(user)); }
  }, [loading, user, navigate]);

  const active = PORTALS.find((p) => p.key === portal);
  const idx = PORTALS.findIndex((p) => p.key === portal);

  const routeAfterAuth = (u) => {
    redirected.current = true;
    if (canAccess(u, portal)) {
      toast.success(`Welcome${portal !== "user" ? `, ${active.label}` : " back"}!`);
      navigate(PATHS[portal]);
    } else if (portal === "attorney" && (u.attorney_status === "pending" || u.attorney_status === "rejected")) {
      navigate("/attorney-pending");
    } else {
      toast.error(`This account doesn't have ${active.label} access yet.`);
      navigate(homeFor(u));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (res?.twofa_required) { setTwofa({ challenge_token: res.challenge_token }); }
      else { routeAfterAuth(res); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await verify2fa(twofa.challenge_token, code);
      routeAfterAuth(u);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  if (twofa) {
    return (
      <AuthShell title="Two-factor verification" subtitle="Enter the 6-digit code from your authenticator app.">
        <form onSubmit={submitCode} className="space-y-4" data-testid="twofa-challenge-form">
          <input data-testid="twofa-code" inputMode="numeric" autoFocus required value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className={`${inputCls} text-center text-2xl tracking-[0.4em] font-semibold`} placeholder="••••••" />
          <button type="submit" disabled={submitting || code.length < 6} data-testid="twofa-verify" className={primaryBtn}>{submitting ? "Verifying…" : "Verify"}</button>
          <button type="button" onClick={() => { setTwofa(null); setCode(""); }} className="w-full text-sm text-slate-500 hover:text-navy">Back to login</button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={active.title} subtitle={active.subtitle}
      footer={<>Don't have an account? <Link to="/register" className="text-cobalt font-medium">Sign up</Link></>}>
      {/* Swipe segmented control */}
      <div className="relative grid bg-slate-100 rounded-full p-1 mb-6" data-testid="portal-switch"
        style={{ gridTemplateColumns: `repeat(${PORTALS.length}, minmax(0, 1fr))` }}>
        <div
          className="absolute top-1 bottom-1 rounded-full bg-white shadow-sm transition-transform duration-300 ease-out"
          style={{ width: `calc((100% - 0.5rem)/${PORTALS.length})`, transform: `translateX(${idx * 100}%)` }}
        />
        {PORTALS.map((p) => {
          const Icon = p.icon;
          const on = p.key === portal;
          return (
            <button key={p.key} type="button" onClick={() => setPortal(p.key)} data-testid={`portal-${p.key}`}
              className={`relative z-10 flex items-center justify-center gap-1 py-2.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${on ? "text-navy" : "text-slate-500"}`}>
              <Icon className="w-4 h-4" /> <span className="hidden xs:inline sm:inline">{p.label}</span>
            </button>
          );
        })}
      </div>

      {portal !== "user" && (
        <div className="mb-5 -mt-1 text-center text-sm" data-testid="portal-apply-hint">
          {portal === "admin" ? (
            <span className="text-slate-500">Admin accounts are provisioned internally — <span className="text-navy font-medium">Request access from your administrator.</span></span>
          ) : (
            <span className="text-slate-500">New here? <Link to={`/register?role=${portal}`} data-testid={`apply-${portal}`} className="text-cobalt font-semibold">Apply as {active.label}</Link></span>
          )}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="you@example.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">Password</label>
            <Link to="/forgot-password" className="text-xs text-cobalt font-medium">Forgot password?</Link>
          </div>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="••••••••" />
        </div>
        <button type="submit" disabled={submitting} data-testid="login-submit" className={primaryBtn}>{submitting ? "Logging in…" : `Log in${portal !== "user" ? ` as ${active.label}` : ""}`}</button>
      </form>

      {portal === "user" && (
        <>
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200" />OR<div className="flex-1 h-px bg-slate-200" /></div>
          <GoogleButton label="Log in with Google" />
        </>
      )}
    </AuthShell>
  );
}
